import React, { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';

const LANG_LABELS = {
  curl: 'cURL',
  python: 'Python',
  javascript: 'JavaScript',
  go: 'Go',
  json: 'JSON',
  bash: 'Bash',
};

/* Minimal regex-based syntax highlighter */
function highlightSyntax(code, lang) {
  if (!code) return '';

  let html = code
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Strings (double and single quoted)
  html = html.replace(
    /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/g,
    '<span style="color:#00E5A0">$1</span>'
  );

  // Numbers
  html = html.replace(
    /\b(\d+\.?\d*)\b/g,
    '<span style="color:#FFB800">$1</span>'
  );

  // Comments
  html = html.replace(
    /(\/\/.*$|#.*$)/gm,
    '<span style="color:var(--text-muted);font-style:italic">$1</span>'
  );

  // Keywords per language
  const keywords = {
    python: ['import', 'from', 'def', 'class', 'return', 'if', 'else', 'for', 'in', 'with', 'as', 'True', 'False', 'None', 'async', 'await'],
    javascript: ['const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'of', 'in', 'async', 'await', 'import', 'from', 'export', 'default', 'true', 'false', 'null', 'new'],
    go: ['package', 'import', 'func', 'var', 'const', 'type', 'struct', 'interface', 'return', 'if', 'else', 'for', 'range', 'nil', 'true', 'false', 'err', 'defer'],
    curl: ['curl'],
  };

  const langKw = keywords[lang] || [];
  if (langKw.length) {
    const kwPattern = new RegExp(`\\b(${langKw.join('|')})\\b`, 'g');
    html = html.replace(kwPattern, '<span style="color:#A855F7;font-weight:600">$1</span>');
  }

  // HTTP methods in curl/bash
  if (lang === 'curl' || lang === 'bash') {
    html = html.replace(
      /\b(GET|POST|PUT|DELETE|PATCH)\b/g,
      '<span style="color:var(--accent-primary);font-weight:700">$1</span>'
    );
  }

  return html;
}

export default function CodeBlock({ examples, title }) {
  const langs = Object.keys(examples);
  const [activeLang, setActiveLang] = useState(langs[0]);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(examples[activeLang]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (headless browser, insecure context, etc.)
      // Silently degrade — button shows no visual feedback
    }
  }, [activeLang, examples]);

  return (
    <div className="code-surface overflow-hidden my-4 glow-accent" style={{ borderRadius: '12px' }}>
      {/* Header bar */}
      <div
        className="flex items-center justify-between px-4 py-0"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        {/* Language tabs */}
        <div className="flex items-center gap-0">
          {langs.map((lang) => (
            <button
              key={lang}
              onClick={() => setActiveLang(lang)}
              className="px-3 py-2 text-xs font-mono transition-colors relative"
              style={{
                color: activeLang === lang ? 'var(--accent-primary)' : 'var(--text-muted)',
              }}
              onMouseEnter={(e) => {
                if (activeLang !== lang) e.target.style.color = 'var(--text-secondary)';
              }}
              onMouseLeave={(e) => {
                if (activeLang !== lang) e.target.style.color = 'var(--text-muted)';
              }}
            >
              {LANG_LABELS[lang] || lang}
              {activeLang === lang && (
                <span
                  className="absolute bottom-0 left-0 right-0 h-px"
                  style={{ background: 'var(--accent-primary)' }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Title + copy */}
        <div className="flex items-center gap-3">
          {title && (
            <span
              className="text-[10px] font-mono uppercase tracking-widest"
              style={{ color: 'var(--text-muted)' }}
            >
              {title}
            </span>
          )}
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5" style={{ color: 'var(--accent-success)' }} />
                <span style={{ color: 'var(--accent-success)' }}>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                Copy
              </>
            )}
          </button>
        </div>
      </div>

      {/* Code body */}
      <div className="overflow-x-auto p-4">
        <pre style={{ fontSize: '13px', lineHeight: '1.7', fontFamily: '"JetBrains Mono", monospace' }}>
          <code
            dangerouslySetInnerHTML={{
              __html: highlightSyntax(examples[activeLang], activeLang),
            }}
          />
        </pre>
      </div>
    </div>
  );
}
